// NdSellerDash.jsx — ypgdash-style dashboard of Nextdoor seller leads.
//
// Shows ONLY leads the AI approved: every tab reads `ndQualifiedLeadsV2`
// (aiVerdict 'good', not hidden). The raw scrape corpus (`ndSellerLeads`, every
// post a search returned) is deliberately not shown — Jackson doesn't want the
// rejects on screen. A qualified doc's `foundBy` lists the seller-search
// accounts whose searches surfaced it.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Avatar,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import SearchIcon from '@mui/icons-material/Search';

import { db, auth } from './firebase';
import {
  cleanPostText,
  dedupeSellerLeads,
  leadInPresetRange,
  compareByScore,
  getPresetRange,
  firstApprovedInWindow,
} from './NdSellerLeadsDashboard';
import logo2 from './leadfeed.png';

const PAGE_SIZE = 100;
// A healthy run takes ~2.5 min per keyword (6 keywords ≈ 15 min); a hard crash
// leaves status='running' forever. Cloud Run's 3600s cap is the outer bound.
const runStaleMs = (run) => Math.min(60 * 60 * 1000, (run.keywordsTotal || 6) * 3 * 60 * 1000 + 6 * 60 * 1000);
const FRESH_POST_MS = 2 * 24 * 60 * 60 * 1000;

const VERDICT_META = {
  good:     { label: 'AI: good',     bg: 'rgba(34,197,94,0.18)',   border: 'rgba(34,197,94,0.5)',   color: '#86efac' },
  unvetted: { label: 'Not vetted',   bg: 'rgba(250,204,21,0.16)',  border: 'rgba(250,204,21,0.5)',  color: '#fde68a' },
};

const RUN_STATUS_META = {
  success: { label: 'Done',    color: '#86efac', bg: 'rgba(34,197,94,0.18)' },
  empty:   { label: '0 found', color: '#fde68a', bg: 'rgba(250,204,21,0.16)' },
  running: { label: 'Running', color: '#7dd3fc', bg: 'rgba(56,189,248,0.16)' },
  hung:    { label: 'Hung',    color: '#fca5a5', bg: 'rgba(239,68,68,0.18)' },
  failed:  { label: 'Failed',  color: '#fca5a5', bg: 'rgba(239,68,68,0.18)' },
};

export function tsToDate(ts) {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  if (ts.seconds) return new Date(ts.seconds * 1000);
  return null;
}

function runHealth(run, nowMs) {
  const started = tsToDate(run.startedAt);
  if (run.status === 'running') {
    return started && nowMs - started.getTime() > runStaleMs(run) ? 'hung' : 'running';
  }
  if (run.status === 'success') return (run.rawCount || 0) === 0 ? 'empty' : 'success';
  return 'failed';
}

// Same tiers and labels as /nd-seller-leads; ranges + blurbs are aiVetter.js's rubric.
export const TIER_META = {
  hot:   { label: '🔥 Hot',   range: '9–10', blurb: 'Contact today',      accent: '#ef4444', color: '#fca5a5', bg: 'rgba(220,38,38,0.18)',  border: 'rgba(220,38,38,0.5)' },
  warm:  { label: '👍 Warm',  range: '7–8',  blurb: 'Contact this week',  accent: '#f59e0b', color: '#fcd34d', bg: 'rgba(217,119,6,0.18)',  border: 'rgba(217,119,6,0.5)' },
  maybe: { label: '💭 Maybe', range: '5–6',  blurb: 'Worth a soft touch', accent: '#94a3b8', color: '#cbd5e1', bg: 'rgba(107,114,128,0.2)', border: 'rgba(148,163,184,0.45)' },
};

export const PRESET_LABELS = { all: 'All time', today: 'Today', '7d': 'Last 7 days', '30d': 'Last 30 days' };

const fmtTime = (d) =>
  d ? d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';

export const fmtWhen = (d) => {
  if (!d) return '';
  if (dayjs(d).isSame(dayjs(), 'day')) return fmtTime(d);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

// Light pill toggle used inside the date bar.
export const lightToggleSx = {
  background: '#fff',
  borderRadius: 999,
  boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.06)',
  '& .MuiToggleButton-root': {
    border: 0,
    borderRadius: 999,
    px: 2,
    py: 0.75,
    fontSize: 13,
    fontWeight: 600,
    color: '#475569',
    textTransform: 'none',
  },
  '& .Mui-selected': { backgroundColor: '#334155 !important', color: '#fff !important' },
};

// Dark pill toggle (view tabs, feed filter).
export const darkToggleSx = {
  backgroundColor: '#020617',
  borderRadius: '999px',
  '& .MuiToggleButton-root': {
    textTransform: 'none',
    fontSize: 13,
    px: 2,
    py: 0.5,
    color: '#e5e7eb',
    border: 'none',
    borderRadius: '999px',
    '&.Mui-selected': { backgroundColor: '#22c55e', color: '#020617' },
    '&.Mui-selected:hover': { backgroundColor: '#16a34a' },
  },
};

// ── Launch DM (scraper-functions/sellerDm.js via exports.sellerDmLaunch) ─────
// Called directly, not through a hosting rewrite — those cut off at 60s and a
// Nextdoor send takes about a minute.
const SELLER_DM_URL = 'https://us-central1-elevate-5a47e.cloudfunctions.net/sellerDmLaunch';
const SENDING_LOCK_MS = 16 * 60 * 1000; // matches sellerDm.js

// sellerDmLaunch takes one request at a time (concurrency 1, maxInstances 1). A request
// that lands while another runs is refused by Cloud Run before sellerDm.js (and its CORS
// headers) can answer, so the browser only sees a network error: "Couldn't reach the DM
// server". So while one lead holds the server, every other row's DM buttons wait.
// holder = { leadId, author, kind: 'preview' | 'send' | 'verify' } or null.
const DmServerContext = createContext({ holder: null, claim: () => () => {} });
const DM_HOLD_VERB = { send: 'sending a DM to', verify: 'checking the delivery to', preview: 'loading the DM for' };
const BUSY_TEXT = "Couldn't reach the DM server. It handles one request at a time, so if another DM is sending, try again in a minute.";

const DM_REASON_TEXT = {
  disabled: 'DMs are switched off (Firestore config/sellerDm).',
  message_not_set: "The DM text hasn't been set yet.",
  proxy_not_configured: 'The DataImpulse proxy is not configured on the server.',
  no_accounts: 'No wholesale DM accounts are set up.',
  already_dmed: 'This person was already DMed.',
  already_sent: 'This lead was already DMed.',
  already_skipped: 'This lead was already skipped.',
  in_progress: 'A DM to this lead is already sending.',
  not_ai_good: 'Only AI-good leads can be DMed.',
  no_target: 'This lead has no post or profile link to DM from.',
  send_failed: 'Nextdoor send failed. You can retry.',
  post_unavailable: 'Nextdoor could not load this post (deleted or hidden). Nothing was sent.',
  preview_required: 'That preview expired. Click Launch DM again to see the current text.',
  draft_changed: 'The message changed since you previewed it. Click Launch DM again.',
  no_name: "Can't auto-check this one: there's no name to match in the inbox. Check the account's Nextdoor chats.",
  stale_client: 'This dashboard page is out of date. Refresh it (Cmd+Shift+R), then click Launch DM again. Nothing was sent.',
  no_message_button: 'This person has Nextdoor messages turned off.',
  sign_in_required: 'Sign in as jackson@jacksonrossi.com to send DMs.',
  bad_token: 'Sign in again to send DMs.',
  not_allowed: 'This account is not allowed to send DMs.',
};

// Every account refused, each for its own reason; the server reports the one that opens soonest.
const DM_LIMIT_REASONS = ['too_soon', 'daily_cap_reached', 'rolling_24h_cap'];
const fmtWait = (min) => (min < 60 ? `${min} min` : `${Math.floor(min / 60)} hr${min % 60 ? ` ${min % 60} min` : ''}`);

const dmReasonText = (r) => {
  if (DM_LIMIT_REASONS.includes(r.reason)) {
    const all = r.accounts > 1 ? `All ${r.accounts} accounts are` : 'All accounts are';
    if (r.waitMinutes == null) return `${all} at their DM limits. Try again later.`;
    const opensAt = fmtWhen(new Date(Date.now() + r.waitMinutes * 60 * 1000));
    return `${all} at their DM limits. The next one opens at ${opensAt} (in ${fmtWait(r.waitMinutes)}).`;
  }
  return DM_REASON_TEXT[r.reason] || r.detail || r.reason || 'Something went wrong.';
};

async function callSellerDm(user, body) {
  const token = await user.getIdToken();
  const res = await fetch(SELLER_DM_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  return { httpStatus: res.status, ...data };
}

/** The sellerDm* fields sellerDm.js writes onto an ndQualifiedLeadsV2 doc. */
// Never offer a second DM to the same person. Lead docs are linked when they share
// a Nextdoor postId, profile URL or name — one post often sits in several docs, and
// one person can post more than once — and if any doc in a person's set was DMed
// (or is sending), every other row for them gets `personDmed` and no Launch DM.
// sellerDm.js refuses those sends server-side too; this keeps the button away.
const normDmUrl = (u) => String(u || '').trim().toLowerCase()
  .replace(/^https?:\/\/(www\.)?/, '').split(/[?#]/)[0].replace(/\/+$/, '');
// Many docs carry no profile URL at all, so two posts by one person can share
// nothing but the name. A full name links on its own; an initial-only name
// ("Susan P.") could be anyone, so it links only within one neighborhood label.
// The author field sometimes holds the whole "Name · Hood · date" byline.
const normName = (s) => String(s || '').split(/\s[·•]\s|\n/)[0].toLowerCase()
  .replace(/[^a-z\s]/g, ' ').replace(/\s+/g, ' ').trim();
function nameKey(d) {
  const n = normName(d.author);
  const parts = n.split(' ');
  if (parts.length < 2 || n === 'unknown') return null;
  if (parts[parts.length - 1].length >= 2) return `name:${n}`;
  const hood = normName(d.neighborhood);
  return hood ? `name:${n}@${hood}` : null;
}
const personKeys = (d) => [
  d.postId && `post:${d.postId}`,
  normDmUrl(d.profileUrl) && `prof:${normDmUrl(d.profileUrl)}`,
  nameKey(d),
  d.sellerDmRecipient && nameKey({ author: d.sellerDmRecipient, neighborhood: d.neighborhood }), // the name Nextdoor showed at send
].filter(Boolean);

function buildDmedPersonIndex(docs) {
  const parent = new Map();
  const find = (k) => { let r = k; while (parent.get(r) !== r) r = parent.get(r); parent.set(k, r); return r; };
  const add = (k) => { if (!parent.has(k)) parent.set(k, k); };
  for (const d of docs || []) {
    const ks = personKeys(d);
    ks.forEach(add);
    for (let i = 1; i < ks.length; i++) { const a = find(ks[0]), b = find(ks[i]); if (a !== b) parent.set(b, a); }
  }
  const dmed = new Map(); // root key -> { account, at }
  for (const d of docs || []) {
    if (d.sellerDmStatus !== 'sent' && d.sellerDmStatus !== 'sending') continue;
    const ks = personKeys(d);
    if (ks.length && !dmed.has(find(ks[0]))) dmed.set(find(ks[0]), { account: d.sellerDmAccount || null, at: tsToDate(d.sellerDmAt || d.sellerDmRequestedAt) });
  }
  // docs of one row -> the person's DM, or null
  return (rowDocs) => {
    for (const d of rowDocs || []) for (const k of personKeys(d)) { const hit = parent.has(k) && dmed.get(find(k)); if (hit) return hit; }
    return null;
  };
}

// A row's DM fields, plus `personDmed` when this person was DMed from another row.
function rowDmFields(row, personDmedOf) {
  const dm = dmFieldsOf(row);
  if (!dm || dm.status === 'sent' || dm.status === 'sending') return dm;
  const prior = personDmedOf(row._docs || [row]);
  return prior ? { ...dm, personDmed: prior } : dm;
}

function dmFieldsOf(doc) {
  if (!doc) return null;
  return {
    // a doc with DM activity always shows it, even if it was re-vetted since
    eligible: (doc.aiVerdict === 'good' && doc.hidden !== true) || !!doc.sellerDmStatus,
    status: doc.sellerDmStatus || null,
    at: tsToDate(doc.sellerDmAt),
    requestedAt: tsToDate(doc.sellerDmRequestedAt),
    account: doc.sellerDmAccount || null,
    message: doc.sellerDmMessage || null,
    unconfirmed: doc.sellerDmUnconfirmed === true,
    skipped: doc.sellerDmSkipped || null,
    error: doc.sellerDmError || null,
  };
}

function DmControl({ lead, user }) {
  const dmServer = useContext(DmServerContext);
  const [busy, setBusy] = useState(null); // null | 'preview' | 'send' | 'verify'
  // { text, hash } while the confirm dialog is open; Send returns the hash so the
  // server only ever sends the exact text shown here
  const [preview, setPreview] = useState(null);
  const [note, setNote] = useState(null); // { tone: 'ok' | 'err', text }
  const dm = lead.dm;
  if (!dm || !dm.eligible) return null;

  // Another lead's request has the DM server: wait instead of colliding with it.
  const heldBy = dmServer.holder && dmServer.holder.leadId !== lead.id ? dmServer.holder : null;
  const heldText = heldBy
    ? `Wait: the DM server is ${DM_HOLD_VERB[heldBy.kind] || 'busy with'} ${heldBy.author || 'another lead'}. It handles one at a time, so this unlocks when it's done.`
    : null;

  // Reads the sending account's inbox for this DM (never sends); on a match the
  // live snapshot clears the warning.
  const checkDelivery = async () => {
    setNote(null);
    setBusy('verify');
    const release = dmServer.claim({ leadId: lead.id, author: lead.author, kind: 'verify' });
    try {
      const r = await callSellerDm(user, { leadId: lead.id, verify: true });
      if (!r.ok) {
        setNote({
          tone: 'err',
          text: r.reason === 'not_in_inbox'
            ? `Not in ${dm.account || 'the account'}'s inbox yet. Check Nextdoor, or try again in a minute.`
            : dmReasonText(r),
        });
      }
    } catch (e) {
      setNote({ tone: 'err', text: BUSY_TEXT });
    } finally {
      release();
      setBusy(null);
    }
  };

  if (dm.status === 'sent') {
    return (
      <Box
        sx={{
          mt: 1,
          p: 1.25,
          borderRadius: 2,
          bgcolor: 'rgba(59,130,246,0.10)',
          border: '1px solid rgba(59,130,246,0.45)',
        }}
      >
        <Typography variant="caption" sx={{ color: '#bfdbfe', fontWeight: 700, display: 'block', mb: 0.5 }}>
          ✓ DMed via {dm.account || 'wholesale'}{dm.at ? ` · ${fmtWhen(dm.at)}` : ''}
        </Typography>
        {dm.unconfirmed && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.75 }}>
            <Typography variant="caption" sx={{ color: '#fde68a' }}>
              Send was clicked but delivery isn't confirmed yet.
            </Typography>
            {user && (
              busy === 'verify' ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <CircularProgress size={12} sx={{ color: '#fde68a' }} />
                  <Typography variant="caption" sx={{ color: '#fde68a' }}>Checking the inbox… (about a minute)</Typography>
                </Box>
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!!heldBy}
                  onClick={checkDelivery}
                  sx={{ ...pillButtonSx, fontSize: 11, px: 1.25, py: 0.1, borderColor: 'rgba(250,204,21,0.6)', color: '#fde68a' }}
                >
                  Check delivery
                </Button>
              )
            )}
            {heldText ? (
              <Typography variant="caption" sx={{ color: '#fde68a', width: '100%' }}>
                {heldText}
              </Typography>
            ) : note && (
              <Typography variant="caption" sx={{ color: note.tone === 'ok' ? '#86efac' : '#fca5a5', width: '100%' }}>
                {note.text}
              </Typography>
            )}
          </Box>
        )}
        <Typography variant="body2" sx={{ color: '#e5e7eb', whiteSpace: 'pre-line', lineHeight: 1.5 }}>
          {dm.message}
        </Typography>
      </Box>
    );
  }

  if (dm.status === 'skipped') {
    return (
      <Chip
        size="small"
        label={dm.skipped === 'no_message_button' ? "Can't DM: messages turned off" : 'Already DMed before (skipped)'}
        sx={{ mt: 1, height: 22, fontSize: 11, bgcolor: 'rgba(148,163,184,0.15)', color: '#cbd5e1' }}
      />
    );
  }

  // DMed from another row (another copy of this post, or another of their posts)
  if (dm.personDmed) {
    const p = dm.personDmed;
    return (
      <Chip
        size="small"
        label={`✓ Already DMed this person${p.account ? ` via ${p.account}` : ''}${p.at ? ` · ${fmtWhen(p.at)}` : ''}`}
        sx={{ mt: 1, height: 22, fontSize: 11, bgcolor: 'rgba(59,130,246,0.12)', color: '#bfdbfe' }}
      />
    );
  }

  const lockLive = dm.status === 'sending'
    && dm.requestedAt && Date.now() - dm.requestedAt.getTime() < SENDING_LOCK_MS;
  if (lockLive || busy === 'send') {
    return (
      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={14} sx={{ color: '#7dd3fc' }} />
        <Typography variant="caption" sx={{ color: '#7dd3fc', fontWeight: 600 }}>
          Sending DM… (takes about a minute)
        </Typography>
      </Box>
    );
  }

  const openPreview = async (refreshNote = null) => {
    setNote(null);
    setBusy('preview');
    const release = dmServer.claim({ leadId: lead.id, author: lead.author, kind: 'preview' });
    try {
      const r = await callSellerDm(user, { leadId: lead.id, preview: true });
      if (r.ok) setPreview({ text: r.message, hash: r.draftHash, note: refreshNote });
      else setNote({ tone: 'err', text: dmReasonText(r) });
    } catch (e) {
      setNote({ tone: 'err', text: BUSY_TEXT });
    } finally {
      release();
      setBusy(null);
    }
  };

  const send = async () => {
    const shownHash = preview?.hash;
    setPreview(null);
    setBusy('send');
    const release = dmServer.claim({ leadId: lead.id, author: lead.author, kind: 'send' });
    try {
      const r = await callSellerDm(user, { leadId: lead.id, draftHash: shownHash });
      // on success the lead's live snapshot flips this row to "✓ DMed"
      if (!r.ok && (r.reason === 'draft_changed' || r.reason === 'preview_required')) {
        // nothing was sent: reopen the box on the current saved text instead of erroring
        await openPreview('The message was updated since you opened it. This is the current text. Press Send DM to send it.');
      } else if (!r.ok) {
        setNote({ tone: 'err', text: dmReasonText(r) });
      }
    } catch (e) {
      setNote({ tone: 'err', text: "Couldn't reach the DM server. Check the lead before retrying, it may still have sent." });
    } finally {
      release();
      setBusy(null);
    }
  };

  const lastError = note?.text || (dm.status === 'failed' || dm.error ? dm.error : null);

  return (
    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      {user ? (
        <Button
          size="small"
          variant="contained"
          disabled={busy === 'preview' || !!heldBy}
          onClick={() => openPreview()}
          sx={{
            borderRadius: 9999,
            textTransform: 'none',
            fontWeight: 700,
            px: 1.75,
            py: 0.4,
            bgcolor: '#22c55e',
            color: '#020617',
            '&:hover': { bgcolor: '#16a34a' },
          }}
        >
          {busy === 'preview' ? 'Loading…' : dm.status === 'failed' ? 'Retry DM' : 'Launch DM'}
        </Button>
      ) : (
        <Button
          size="small"
          variant="outlined"
          component={RouterLink}
          to="/sign-in"
          sx={{ ...pillButtonSx, fontSize: 12, px: 1.75, py: 0.3 }}
        >
          Sign in to DM
        </Button>
      )}
      {heldText ? (
        <Typography variant="caption" sx={{ color: '#fde68a' }}>
          {heldText}
        </Typography>
      ) : lastError && (
        <Typography variant="caption" sx={{ color: note?.tone === 'ok' ? '#86efac' : '#fca5a5' }}>
          {lastError}
        </Typography>
      )}

      <Dialog
        open={preview != null}
        onClose={() => setPreview(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#0b1220', color: '#f9fafb', backgroundImage: 'none', border: '1px solid rgba(148,163,184,0.25)' } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Send this DM to {lead.author || 'this person'}?</DialogTitle>
        <DialogContent>
          <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mb: 1 }}>
            Sent on Nextdoor from a wholesale account. It can't be unsent.
          </Typography>
          {preview?.note && (
            <Typography variant="body2" sx={{ color: '#fde68a', mb: 1 }}>
              {preview.note}
            </Typography>
          )}
          {heldText && (
            <Typography variant="body2" sx={{ color: '#fde68a', mb: 1 }}>
              {heldText}
            </Typography>
          )}
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: 'rgba(15,23,42,1)',
              border: '1px solid rgba(55,65,81,0.95)',
              whiteSpace: 'pre-line',
              lineHeight: 1.55,
              fontSize: 14,
            }}
          >
            {preview?.text}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPreview(null)} sx={{ textTransform: 'none', color: '#cbd5e1' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!!heldBy}
            onClick={send}
            sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#22c55e', color: '#020617', '&:hover': { bgcolor: '#16a34a' } }}
          >
            Send DM
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export function TierChip({ tier, score }) {
  const t = TIER_META[tier];
  const s = Number.isFinite(score) ? `${score}/10` : null;
  if (!t && !s) return null;
  return (
    <Chip
      size="small"
      label={t ? `${t.label}${s ? ` · ${s}` : ''}` : s}
      sx={{
        height: 22,
        fontSize: 11,
        fontWeight: 700,
        bgcolor: t ? t.bg : 'rgba(56,189,248,0.16)',
        color: t ? t.color : '#7dd3fc',
        border: t ? `1px solid ${t.border}` : 'none',
      }}
    />
  );
}

const darkCardSx = (accent) => ({
  borderRadius: 3,
  bgcolor: 'rgba(15,23,42,0.9)',
  border: `1px solid ${accent}59`,
  boxShadow: '0 15px 40px rgba(2,6,23,0.35)',
  position: 'relative',
  overflow: 'hidden',
  color: '#f9fafb',
  minHeight: 132,
  cursor: 'pointer',
});

export function StatCard({ accent, title, value, sub, active, hint, onClick }) {
  return (
    <Card
      elevation={0}
      onClick={onClick}
      sx={{
        ...darkCardSx(accent),
        outline: active ? `2px solid ${accent}` : 'none',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: `linear-gradient(135deg, ${accent}4d, ${accent}0d)`,
          pointerEvents: 'none',
        }}
      />
      <CardContent sx={{ position: 'relative' }}>
        <Typography variant="caption" sx={{ color: '#f9fafb' }}>
          {title}
        </Typography>
        <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5, color: '#ffffff' }}>
          {value}
        </Typography>
        <Typography variant="body2" sx={{ color: '#f9fafb', mt: 0.5 }}>
          {sub}
        </Typography>
        {hint && (
          <Typography variant="caption" sx={{ color: accent, mt: 0.75, display: 'block' }}>
            {hint}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function LeadRow({ lead, rank, user }) {
  const [expanded, setExpanded] = useState(false);
  const v = VERDICT_META[lead.verdict];
  const text = lead.text || '—';
  const long = text.length > 320;
  const initials =
    (lead.author || '')
      .split(' ')
      .filter(Boolean)
      .map((p) => p[0])
      .join('')
      .slice(0, 2) || '?';
  const foundAt = lead.firstApprovedAt || lead.scrapedAt;
  const fresh = lead.postDate && foundAt
    && foundAt.getTime() - lead.postDate.getTime() < FRESH_POST_MS;

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1 }}>
      {rank != null && (
        <Typography
          sx={{
            minWidth: 40,
            flexShrink: 0,
            pt: 0.75,
            textAlign: 'right',
            fontWeight: 800,
            fontSize: 17,
            color: rank <= 3 ? '#facc15' : '#64748b',
          }}
        >
          #{rank}
        </Typography>
      )}
      <Avatar
        src={lead.profilePhoto || undefined}
        alt={lead.author || 'Lead'}
        sx={{
          width: 40,
          height: 40,
          bgcolor: lead.profilePhoto ? '#111827' : 'rgba(16,185,129,0.9)',
          fontSize: 14,
          fontWeight: 600,
          border: '1px solid rgba(31,41,55,1)',
        }}
      >
        {!lead.profilePhoto ? initials : null}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Header row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
            mb: 0.5,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap sx={{ color: '#ffffff' }}>
              {lead.author || 'Unknown'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
              {lead.firstApprovedAt ? `First approved ${fmtWhen(lead.firstApprovedAt)}` : `Scraped ${fmtWhen(lead.scrapedAt)}`}
              {lead.postDateLabel ? ` · posted ${lead.postDateLabel}` : ''}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {/* Keyword pill */}
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.3,
                borderRadius: 9999,
                bgcolor: 'rgba(15,23,42,1)',
                border: '1px solid rgba(55,65,81,0.95)',
              }}
            >
              <SearchIcon sx={{ fontSize: 16, color: '#22c55e' }} />
              <Typography variant="caption" sx={{ color: '#ffffff', fontWeight: 500 }}>
                {lead.keyword || '—'}
              </Typography>
            </Box>

            {fresh && (
              <Chip
                size="small"
                label="New post"
                sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: '#FFD54F', color: '#000' }}
              />
            )}

            {lead.verdict === 'good' && <TierChip tier={lead.aiTier} score={lead.aiScore} />}

            {/* every row is AI-approved, so only the exceptions (not vetted) get a verdict chip */}
            {lead.verdict !== 'good' && (
              <Chip
                size="small"
                label={v.label}
                sx={{
                  height: 22,
                  fontSize: 11,
                  fontWeight: 700,
                  bgcolor: v.bg,
                  color: v.color,
                  border: `1px solid ${v.border}`,
                }}
              />
            )}
          </Box>
        </Box>

        {/* Post text */}
        <Typography
          variant="body2"
          sx={{ whiteSpace: 'pre-line', color: '#e5e7eb', lineHeight: 1.5 }}
        >
          {long && !expanded ? `${text.slice(0, 320)}…` : text}
          {long && (
            <Box
              component="span"
              onClick={() => setExpanded((x) => !x)}
              sx={{ ml: 0.75, color: '#7dd3fc', cursor: 'pointer', fontWeight: 600 }}
            >
              {expanded ? 'less' : 'more'}
            </Box>
          )}
        </Typography>

        {lead.verdict === 'good' && lead.aiReason && (
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#86efac' }}>
            AI: {lead.aiReason}
          </Typography>
        )}

        {lead.verdict === 'good' && <DmControl lead={lead} user={user} />}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          {lead.neighborhood && (
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              {lead.neighborhood}
            </Typography>
          )}
          {lead.foundBy?.length > 0 && (
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              · found by {lead.foundBy.join(', ')}
            </Typography>
          )}
          {lead.postUrl && (
            <Typography
              component="a"
              href={lead.postUrl}
              target="_blank"
              rel="noreferrer"
              variant="caption"
              sx={{ ml: 'auto', color: '#22c55e', textDecoration: 'none', fontWeight: 700 }}
            >
              OPEN ↗
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export const feedPaperSx = {
  borderRadius: 3,
  bgcolor: 'rgba(15,23,42,0.98)',
  border: '1px solid rgba(55,65,81,0.95)',
  boxShadow: '0 18px 50px rgba(0,0,0,0.85)',
  p: 2.5,
};

// "Highest scoring" tab — ranked exactly like /nd-seller-leads, through the
// same exported helpers: the whole qualified set, deduped, hidden leads out,
// its All/Today/7d/30d window on post date, then AI score high→low with the
// newest post first on ties.
function HighestScoring({ qualDocs, preset, error, user }) {
  const [tier, setTier] = useState('all'); // 'all' | 'hot' | 'warm' | 'maybe'
  const [visible, setVisible] = useState(PAGE_SIZE);
  const presetRange = useMemo(() => getPresetRange(preset), [preset]);
  const presetLabel = PRESET_LABELS[preset];
  const loading = qualDocs === null;

  useEffect(() => { setVisible(PAGE_SIZE); }, [preset, tier]);

  const personDmedOf = useMemo(() => buildDmedPersonIndex(qualDocs), [qualDocs]);
  const ranked = useMemo(() => {
    if (!qualDocs) return [];
    return dedupeSellerLeads(qualDocs)
      .filter((l) => l.hidden !== true && leadInPresetRange(l, presetRange))
      .sort(compareByScore)
      .map((l, i) => {
        const kws = l.keywords || [];
        return {
          id: l.id,
          rank: i + 1,
          author: l.author,
          profilePhoto: l.profilePhoto,
          neighborhood: l.neighborhood,
          postUrl: l.postUrl,
          postDateLabel: l.postDateLabel,
          postDate: tsToDate(l.postDate),
          keyword: kws.length > 1 ? `${kws[0]} +${kws.length - 1}` : kws[0],
          text: cleanPostText(l),
          scrapedAt: tsToDate(l.scrapedAt),
          verdict: l.aiVerdict === 'good' ? 'good' : 'unvetted',
          foundBy: l.foundBy || [],
          aiReason: l.aiReason,
          aiScore: l.aiScore,
          aiTier: l.aiTier,
          dm: rowDmFields(l, personDmedOf),
        };
      });
  }, [qualDocs, presetRange, personDmedOf]);

  const tierCounts = useMemo(() => {
    const c = { hot: 0, warm: 0, maybe: 0 };
    ranked.forEach((l) => { if (TIER_META[l.aiTier]) c[l.aiTier] += 1; });
    return c;
  }, [ranked]);

  const shown = tier === 'all' ? ranked : ranked.filter((l) => l.aiTier === tier);

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            accent="#38bdf8"
            title={`Ranked Leads · ${presetLabel}`}
            value={loading ? '—' : ranked.length}
            sub="AI-approved, best first"
            active={tier === 'all'}
            hint={tier === 'all' ? '▾ showing all' : '▸ show all'}
            onClick={() => setTier('all')}
          />
        </Grid>
        {['hot', 'warm', 'maybe'].map((k) => (
          <Grid item xs={12} sm={6} md={3} key={k}>
            <StatCard
              accent={TIER_META[k].accent}
              title={`${TIER_META[k].label} · score ${TIER_META[k].range}`}
              value={loading ? '—' : tierCounts[k]}
              sub={TIER_META[k].blurb}
              active={tier === k}
              hint={tier === k ? `▾ showing ${k} only` : `▸ show ${k} only`}
              onClick={() => setTier((cur) => (cur === k ? 'all' : k))}
            />
          </Grid>
        ))}
      </Grid>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Paper elevation={0} sx={feedPaperSx}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
            mb: 1,
          }}
        >
          <Typography variant="subtitle1" fontWeight={700} sx={{ letterSpacing: 0.5, color: '#ffffff' }}>
            Highest scoring · {presetLabel}
            {tier !== 'all' ? ` · ${TIER_META[tier].label}` : ''}
          </Typography>
          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
            Ranked by AI score, newest post first on ties — same order as the full lead browser
          </Typography>
        </Box>

        {loading ? (
          <Typography variant="body2" sx={{ color: '#f9fafb' }}>
            Loading…
          </Typography>
        ) : shown.length === 0 ? (
          <Typography variant="body2" sx={{ color: '#f9fafb' }}>
            No ranked leads for {presetLabel.toLowerCase()}.
          </Typography>
        ) : (
          <Box>
            {shown.slice(0, visible).map((lead, idx) => (
              <Box key={lead.id}>
                {idx > 0 && <Divider sx={{ my: 1, borderColor: 'rgba(31,41,55,1)' }} />}
                <LeadRow lead={lead} rank={lead.rank} user={user} />
              </Box>
            ))}

            {shown.length > visible && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button variant="outlined" onClick={() => setVisible((n) => n + PAGE_SIZE)} sx={pillButtonSx}>
                  Show more ({shown.length - visible} left)
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Paper>
    </>
  );
}

export const pillButtonSx = {
  textTransform: 'none',
  borderRadius: 9999,
  borderColor: 'rgba(148,163,184,0.6)',
  color: '#f9fafb',
  fontSize: 13,
  px: 2.5,
  '&:hover': {
    borderColor: '#22c55e',
    background: 'linear-gradient(135deg, rgba(34,197,94,0.08), rgba(59,130,246,0.12))',
  },
};

const scrapedMs = (d) => tsToDate(d.scrapedAt)?.getTime() || 0;

// One row of the windowed tabs. `doc` is the approved doc behind the row's time,
// score and tier; its DM state is the whole post's.
const feedRow = (g, doc, personDmedOf) => ({
  id: g.id,
  author: g.author,
  profilePhoto: g.profilePhoto,
  neighborhood: g.neighborhood,
  postUrl: g.postUrl,
  postDateLabel: g.postDateLabel,
  postDate: tsToDate(g.postDate),
  keyword: doc.lastKeyword || (doc.keywords || [])[0],
  text: cleanPostText(g),
  scrapedAt: tsToDate(doc.scrapedAt),
  verdict: 'good',
  aiReason: doc.aiReason,
  aiScore: doc.aiScore,
  aiTier: doc.aiTier,
  foundBy: g.foundBy || [],
  dm: rowDmFields(g, personDmedOf),
});

export default function NdSellerDash() {
  // Opens on today's new leads. On the ranking they sit below every older high scorer,
  // so a day of 5s and 6s looked like no new leads at all.
  const [view, setView] = useState('new'); // 'top' | 'feed' | 'new'
  const [preset, setPreset] = useState('all'); // Highest-scoring window, same presets as /nd-seller-leads
  const [dateMode, setDateMode] = useState('single'); // 'single' | 'range'
  const [singleDate, setSingleDate] = useState(dayjs());
  const [rangeStart, setRangeStart] = useState(dayjs().subtract(6, 'day'));
  const [rangeEnd, setRangeEnd] = useState(dayjs());

  const [qualDocs, setQualDocs] = useState(null);
  const [runs, setRuns] = useState([]);
  const [error, setError] = useState('');
  const [feedTier, setFeedTier] = useState('all'); // 'all' | 'hot' | 'warm' | 'maybe'
  const [showRuns, setShowRuns] = useState(false);
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [nowMs, setNowMs] = useState(Date.now());
  const [user, setUser] = useState(() => auth.currentUser); // Launch DM needs a signed-in Jackson

  useEffect(() => onAuthStateChanged(auth, setUser), []);

  // Re-evaluate "Hung" runs without waiting for a snapshot.
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 30 * 1000);
    return () => clearInterval(t);
  }, []);

  // A tab left open overnight moves on to the new day, unless another day was picked.
  const calendarDay = dayjs(nowMs).format('YYYY-MM-DD');
  const lastCalendarDay = useRef(calendarDay);
  useEffect(() => {
    const prev = lastCalendarDay.current;
    if (prev === calendarDay) return;
    lastCalendarDay.current = calendarDay;
    setSingleDate((cur) => (cur.format('YYYY-MM-DD') === prev ? dayjs() : cur));
  }, [calendarDay]);

  // Who holds the one-at-a-time DM server (DmServerContext): this tab's own request in
  // flight, else a send in progress from anywhere (its live 'sending' lock).
  const [dmLocal, setDmLocal] = useState(null);
  const claimDmServer = useCallback((h) => {
    const token = {};
    setDmLocal({ ...h, token });
    return () => setDmLocal((cur) => (cur && cur.token === token ? null : cur));
  }, []);
  const dmRemote = useMemo(() => {
    const d = (qualDocs || []).find((x) => {
      const at = x.sellerDmStatus === 'sending' ? tsToDate(x.sellerDmRequestedAt) : null;
      return at && nowMs - at.getTime() < SENDING_LOCK_MS;
    });
    return d ? { leadId: d.id, author: String(d.author || '').split(/\s[·•]\s|\n/)[0].trim() || null, kind: 'send' } : null;
  }, [qualDocs, nowMs]);
  const dmServer = useMemo(() => ({ holder: dmLocal || dmRemote, claim: claimDmServer }), [dmLocal, dmRemote, claimDmServer]);

  const { filterStart, filterEnd, label } = useMemo(() => {
    if (dateMode === 'range') {
      // tolerate From > To rather than querying an empty window
      const [lo, hi] = rangeStart.isAfter(rangeEnd) ? [rangeEnd, rangeStart] : [rangeStart, rangeEnd];
      const s = lo.startOf('day');
      const e = hi.startOf('day').add(1, 'day'); // exclusive end
      return {
        filterStart: s.toDate(),
        filterEnd: e.toDate(),
        label: `${s.format('MMM D')} – ${hi.format('MMM D')}`,
      };
    }
    const s = singleDate.startOf('day');
    return {
      filterStart: s.toDate(),
      filterEnd: s.add(1, 'day').toDate(),
      label: s.format('MMM D, YYYY'),
    };
  }, [dateMode, singleDate, rangeStart, rangeEnd]);

  const isToday = dateMode === 'single' && singleDate.isSame(dayjs(), 'day');
  const statLabel = dateMode === 'range' ? label : (isToday ? 'Today' : singleDate.format('MMM D'));

  // The whole qualified set: every tab reads only from here (approved leads only).
  useEffect(() => {
    return onSnapshot(
      collection(db, 'ndQualifiedLeadsV2'),
      (snap) => setQualDocs(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => {
        console.error('[sellerdash]', err);
        setError(err.message || String(err));
      },
    );
  }, []);

  // Scrape runs in the window (for the health line). The leads themselves come
  // from the qualified set above, filtered to the window in memory.
  useEffect(() => {
    // The pickers already drop invalid dates; this keeps a bad window out of
    // where(), which throws synchronously on an Invalid Date.
    if (Number.isNaN(filterStart.getTime()) || Number.isNaN(filterEnd.getTime())) return undefined;

    setRuns([]);
    setVisible(PAGE_SIZE);

    return onSnapshot(
      query(
        collection(db, 'ndSellerScrapeRuns'),
        where('startedAt', '>=', filterStart),
        where('startedAt', '<', filterEnd),
        orderBy('startedAt', 'desc'),
      ),
      (snap) => setRuns(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => {
        console.error('[sellerdash]', err);
        setError(err.message || String(err));
      },
    );
  }, [filterStart, filterEnd]);

  const loading = qualDocs === null;

  const runRows = useMemo(
    () =>
      [...runs]
        .map((r) => ({ ...r, health: runHealth(r, nowMs), started: tsToDate(r.startedAt) }))
        .sort((a, b) => (b.started?.getTime() || 0) - (a.started?.getTime() || 0)),
    [runs, nowMs],
  );
  const runsOk = runRows.filter((r) => r.health === 'success').length;
  const activeRun = runRows.find((r) => r.health === 'running');
  const runProblems = [
    ['hung', 'hung'],
    ['failed', 'failed'],
    ['empty', 'returned 0'],
  ]
    .map(([k, word]) => {
      const n = runRows.filter((r) => r.health === k).length;
      return n ? `${n} ${word}` : null;
    })
    .filter(Boolean);

  // Approved leads only (AI-good, not hidden), newest approval first, ONE row per
  // post — the same grouping as Highest scoring, so a post several docs hold shows
  // once. A qualified doc's scrapedAt is rewritten each time a scrape re-approves
  // it, so the window means "approved by a scrape in this window"; the row's time,
  // score and tier are that newest approval, its DM state the group's.
  const personDmedOf = useMemo(() => buildDmedPersonIndex(qualDocs), [qualDocs]);
  const latestApproved = useMemo(() => {
    if (loading) return [];
    return dedupeSellerLeads(qualDocs)
      .map((g) => {
        if (g.hidden === true) return null;
        const approved = g._docs
          .filter((d) => {
            if (d.aiVerdict !== 'good' || d.hidden === true) return false;
            const t = tsToDate(d.scrapedAt);
            return t && t >= filterStart && t < filterEnd;
          })
          .sort((a, b) => scrapedMs(b) - scrapedMs(a));
        return approved.length ? feedRow(g, approved[0], personDmedOf) : null;
      })
      .filter(Boolean)
      .sort((a, b) => (b.scrapedAt?.getTime() || 0) - (a.scrapedAt?.getTime() || 0));
  }, [loading, qualDocs, filterStart, filterEnd, personDmedOf]);

  // "New" tab: posts first approved in the window, so a re-found old lead stays on
  // the day it was first approved. The row's score and tier are its latest approval.
  const { firstApproved, unstampedPosts } = useMemo(() => {
    if (loading) return { firstApproved: [], unstampedPosts: 0 };
    const { rows, unstamped } = firstApprovedInWindow(qualDocs, filterStart, filterEnd);
    return {
      firstApproved: rows
        .map(({ group, approved, firstApprovedAt }) => ({
          ...feedRow(group, [...approved].sort((a, b) => scrapedMs(b) - scrapedMs(a))[0], personDmedOf),
          firstApprovedAt,
        }))
        .sort((a, b) => b.firstApprovedAt - a.firstApprovedAt),
      unstampedPosts: unstamped,
    };
  }, [loading, qualDocs, filterStart, filterEnd, personDmedOf]);

  const isNew = view === 'new';
  const leads = isNew ? firstApproved : latestApproved;

  const tierCounts = useMemo(() => {
    const c = { hot: 0, warm: 0, maybe: 0 };
    leads.forEach((l) => { if (TIER_META[l.aiTier]) c[l.aiTier] += 1; });
    return c;
  }, [leads]);

  const filtered = feedTier === 'all' ? leads : leads.filter((l) => l.aiTier === feedTier);

  const toggleTier = (next) => {
    setFeedTier((cur) => (cur === next ? 'all' : next));
    setVisible(PAGE_SIZE);
  };

  // ─────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────
  return (
    <DmServerContext.Provider value={dmServer}>
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        px: 2,
        py: 4,
        background:
          'radial-gradient(circle at 0% 0%, #1e293b 0, #020617 35%, #020617 60%, #020617 100%)',
        color: '#e5e7eb',
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 1280 }}>
        {/* Top Center Logo */}
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mb: 4 }}>
          <Box
            sx={{
              p: 2.5,
              borderRadius: 3,
              bgcolor: '#ffffff',
              border: '1px solid rgba(0,0,0,0.08)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            }}
          >
            <img src={logo2} alt="LeadFeed" style={{ height: 70, display: 'block' }} />
          </Box>
        </Box>

        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            px: 3,
            py: 2.5,
            borderRadius: 3,
            bgcolor: 'rgba(15,23,42,0.9)',
            border: '1px solid rgba(148,163,184,0.35)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
            backdropFilter: 'blur(18px)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              mb: 2.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 2,
                  bgcolor: '#ffffff',
                  border: '1px solid rgba(148,163,184,0.5)',
                  boxShadow: '0 0 30px rgba(34,197,94,0.25)',
                }}
              >
                <img src={logo2} alt="LeadFeed" style={{ height: 40, display: 'block' }} />
              </Box>
              <Box>
                <Typography
                  variant="h5"
                  fontWeight={800}
                  sx={{ letterSpacing: 0.3, textTransform: 'uppercase', color: '#f9fafb' }}
                >
                  ND Seller Leads
                </Typography>
                <Typography variant="body2" sx={{ color: '#9ca3af', mt: 0.5 }}>
                  Nextdoor pre-distress sellers · AI vetted
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              {/* Pulsing live indicator — pulses while a scrape run is in flight */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: '#22c55e',
                    boxShadow: '0 0 15px rgba(34,197,94,0.9)',
                    animation: activeRun ? 'pulseDot 1.4s ease-in-out infinite' : 'none',
                    '@keyframes pulseDot': {
                      '0%': { transform: 'scale(1)', opacity: 1 },
                      '50%': { transform: 'scale(1.6)', opacity: 0.5 },
                      '100%': { transform: 'scale(1)', opacity: 1 },
                    },
                  }}
                />
                <Typography variant="caption" sx={{ color: '#f9fafb' }}>
                  {activeRun
                    ? `Live · Scraping (${activeRun.keywordsDone || 0}/${activeRun.keywordsTotal || '?'} keywords)`
                    : 'Live · Up to date'}
                </Typography>
              </Box>

              <Button variant="outlined" component={RouterLink} to="/nd-seller-leads" sx={pillButtonSx}>
                Full lead browser
              </Button>
            </Box>
          </Box>

          {/* Date bar */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Paper
              elevation={0}
              sx={{
                px: 2.5,
                py: 2,
                borderRadius: 3,
                backgroundColor: '#f8fafc',
                boxShadow: '0 6px 20px rgba(0,0,0,0.06)',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {view === 'top' ? (
                  <>
                    <ToggleButtonGroup
                      exclusive
                      value={preset}
                      onChange={(_, val) => val && setPreset(val)}
                      sx={lightToggleSx}
                    >
                      {Object.entries(PRESET_LABELS).map(([key, text]) => (
                        <ToggleButton key={key} value={key}>{text}</ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                    <Chip
                      label="By post date"
                      sx={{ backgroundColor: '#e2e8f0', color: '#334155', fontWeight: 700, borderRadius: 2 }}
                    />
                  </>
                ) : (
                <>
                <ToggleButtonGroup
                  exclusive
                  value={dateMode}
                  onChange={(_, val) => val && setDateMode(val)}
                  sx={lightToggleSx}
                >
                  <ToggleButton value="single">One date</ToggleButton>
                  <ToggleButton value="range">Range</ToggleButton>
                </ToggleButtonGroup>

                {dateMode === 'single' ? (
                  <DatePicker
                    label="Date"
                    value={singleDate}
                    onChange={(v) => v && v.isValid() && setSingleDate(v)}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { minWidth: 160, backgroundColor: '#fff', borderRadius: 2 },
                      },
                    }}
                  />
                ) : (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                    <DatePicker
                      label="From"
                      value={rangeStart}
                      onChange={(v) => v && v.isValid() && setRangeStart(v)}
                      slotProps={{
                        textField: {
                          size: 'small',
                          sx: { minWidth: 150, backgroundColor: '#fff', borderRadius: 2 },
                        },
                      }}
                    />
                    <DatePicker
                      label="To"
                      value={rangeEnd}
                      onChange={(v) => v && v.isValid() && setRangeEnd(v)}
                      slotProps={{
                        textField: {
                          size: 'small',
                          sx: { minWidth: 150, backgroundColor: '#fff', borderRadius: 2 },
                        },
                      }}
                    />
                  </Box>
                )}

                <Chip
                  label={label}
                  sx={{ backgroundColor: '#e2e8f0', color: '#334155', fontWeight: 700, borderRadius: 2 }}
                />
                </>
                )}
              </Box>
            </Paper>
          </LocalizationProvider>
        </Paper>

        {/* View tabs */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <ToggleButtonGroup
            exclusive
            value={view}
            onChange={(_, val) => val && setView(val)}
            sx={{
              ...darkToggleSx,
              border: '1px solid rgba(55,65,81,0.95)',
              '& .MuiToggleButton-root': {
                ...darkToggleSx['& .MuiToggleButton-root'],
                fontSize: 14,
                fontWeight: 700,
                px: 3,
                py: 0.9,
              },
            }}
          >
            <ToggleButton value="top">🏆 Highest scoring</ToggleButton>
            <ToggleButton value="feed">🆕 Latest approved</ToggleButton>
            <ToggleButton value="new">✨ New</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {view === 'top' && <HighestScoring qualDocs={qualDocs} preset={preset} error={error} user={user} />}

        {view !== 'top' && (
        <>
        {/* Metrics row — approved leads only */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              accent="#38bdf8"
              title={`${isNew ? 'New' : 'Approved'} · ${statLabel}`}
              value={loading ? '—' : leads.length}
              sub={isNew
                ? (isToday ? 'First approved today' : 'First approved in this window')
                : (isToday ? 'AI-approved leads today' : 'AI-approved in this window')}
              active={feedTier === 'all'}
              hint={feedTier === 'all' ? '▾ showing all' : '▸ show all'}
              onClick={() => { setFeedTier('all'); setVisible(PAGE_SIZE); }}
            />
          </Grid>
          {['hot', 'warm', 'maybe'].map((k) => (
            <Grid item xs={12} sm={6} md={3} key={k}>
              <StatCard
                accent={TIER_META[k].accent}
                title={`${TIER_META[k].label} · score ${TIER_META[k].range}`}
                value={loading ? '—' : tierCounts[k]}
                sub={TIER_META[k].blurb}
                active={feedTier === k}
                hint={feedTier === k ? `▾ showing ${k} only` : `▸ show ${k} only`}
                onClick={() => toggleTier(k)}
              />
            </Grid>
          ))}
        </Grid>

        {/* Scrape health, one line (expands to the run list) */}
        <Box
          onClick={() => setShowRuns((x) => !x)}
          sx={{
            mb: 3,
            px: 2,
            py: 1,
            borderRadius: 2,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
            bgcolor: 'rgba(15,23,42,0.9)',
            border: '1px solid rgba(59,130,246,0.35)',
          }}
        >
          <Typography variant="body2" sx={{ color: '#f9fafb', fontWeight: 600 }}>
            Scrape runs · {statLabel}: {runsOk}/{runRows.length} finished
          </Typography>
          <Typography variant="caption" sx={{ color: runProblems.length ? '#fca5a5' : '#94a3b8' }}>
            {!runRows.length ? 'No runs' : runProblems.length ? runProblems.join(' · ') : 'all clean'}
          </Typography>
          <Typography variant="caption" sx={{ color: '#7dd3fc', ml: 'auto' }}>
            {showRuns ? '▾ hide runs' : '▸ show runs'}
          </Typography>
        </Box>

        {/* Run list behind the runs card */}
        {showRuns && (
          <Box
            sx={{
              mb: 3,
              borderRadius: 3,
              bgcolor: 'rgba(15,23,42,0.9)',
              border: '1px solid rgba(59,130,246,0.35)',
              overflow: 'hidden',
            }}
          >
            {!runRows.length && (
              <Typography variant="body2" sx={{ color: '#94a3b8', p: 2 }}>
                No scrape runs in this window.
              </Typography>
            )}
            {runRows.map((r) => {
              const m = RUN_STATUS_META[r.health];
              return (
                <Box
                  key={r.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    px: 2,
                    py: 1.25,
                    borderBottom: '1px solid rgba(148,163,184,0.10)',
                  }}
                >
                  <Typography variant="body2" sx={{ color: '#f9fafb', fontWeight: 600, minWidth: 120 }}>
                    {r.started
                      ? r.started.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
                      : '—'}
                  </Typography>
                  <Chip
                    size="small"
                    label={r.account || '—'}
                    sx={{ height: 20, fontSize: 11, bgcolor: 'rgba(148,163,184,0.15)', color: '#e5e7eb' }}
                  />
                  <Chip
                    size="small"
                    label={m.label}
                    sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: m.bg, color: m.color }}
                  />
                  <Typography variant="caption" sx={{ color: '#94a3b8', ml: 'auto' }}>
                    {r.keywordsDone || 0}/{r.keywordsTotal || '?'} keywords · {r.rawCount || 0} scraped ·{' '}
                    <Box component="span" sx={{ color: '#86efac' }}>{r.qualifiedCount || 0} good</Box>
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}

        {loading && !error && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress
              sx={{
                height: 3,
                borderRadius: 9999,
                bgcolor: 'rgba(15,23,42,0.8)',
                '& .MuiLinearProgress-bar': {
                  background: 'linear-gradient(90deg, #22c55e, #4ade80, #22c55e)',
                },
              }}
            />
          </Box>
        )}

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {/* Lead feed */}
        <Paper elevation={0} sx={feedPaperSx}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1.5,
              mb: 1,
            }}
          >
            <Typography variant="subtitle1" fontWeight={700} sx={{ letterSpacing: 0.5, color: '#ffffff' }}>
              {isNew ? 'First approved' : 'Latest approved'} · {statLabel}
              {feedTier !== 'all' ? ` · ${TIER_META[feedTier].label}` : ''}
            </Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
              {isNew
                ? 'Newest first · a re-found lead stays on the day it was first approved'
                : 'Newest first · only leads the AI approved'}
            </Typography>
          </Box>

          {isNew && unstampedPosts > 0 && (
            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#fde68a' }}>
              {unstampedPosts} approved {unstampedPosts === 1 ? 'post has' : 'posts have'} no first-approved time yet, so {unstampedPosts === 1 ? "it's" : "they're"} left out.
            </Typography>
          )}

          {loading ? (
            <Typography variant="body2" sx={{ color: '#f9fafb' }}>
              Loading…
            </Typography>
          ) : filtered.length === 0 ? (
            <Typography variant="body2" sx={{ color: '#f9fafb' }}>
              No {feedTier === 'all' ? (isNew ? 'new' : 'approved') : TIER_META[feedTier].label} leads for {label}.
            </Typography>
          ) : (
            <Box>
              {filtered.slice(0, visible).map((lead, idx) => (
                <Box key={lead.id}>
                  {idx > 0 && <Divider sx={{ my: 1, borderColor: 'rgba(31,41,55,1)' }} />}
                  <LeadRow lead={lead} user={user} />
                </Box>
              ))}

              {filtered.length > visible && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Button variant="outlined" onClick={() => setVisible((n) => n + PAGE_SIZE)} sx={pillButtonSx}>
                    Show more ({filtered.length - visible} left)
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Paper>
        </>
        )}
      </Box>
    </Box>
    </DmServerContext.Provider>
  );
}
